import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import isHotkey from 'is-hotkey';
import type { GanttData, Task } from './types';
import { useGanttState, UseGanttStateReturn } from './hooks/useGanttState';
import { useGanttImportExport } from './hooks/useGanttImportExport';
import { GanttContainer } from './components/GanttContainer';
import { TaskEditDialog } from './components/TaskEditDialog';
import { ImportTextDialog } from './components/ImportTextDialog';
import { ImportJsonDialog } from './components/ImportJsonDialog';
import { OccupyFreeSpace } from '../../toolympus/components/primitives';
import { GanttContext } from './GanttContext';
import { diffDays, maxIso, minIso, todayIso } from './dateUtils';
import { COLUMN_WIDTH, TIMELINE_DAYS } from './constants';

type Props = {
  initialData?: GanttData;
};

export const GanttEditorX = ({ initialData }: Props) => {
  const data = useGanttState(initialData);
  return <GanttEditor data={data} />
}

export const GanttEditor: React.FC<{ data: UseGanttStateReturn, save?: () => void }> = ({ data: dataX, save }) => {
  const {
    data,
    setData,
    addTask,
    addTasksBulk,
    updateTask,
    removeTask,
    moveTaskDates,
    moveTaskToRow,
    createRow,
    addDependency,
    removeDependency,
    autoArrangeInPlace,
    autoScheduleInPlace,
    expandToSingleTaskRowsInPlace,
    shiftUnresolvedTasksInPlace,
    shiftFromUnresolvedDependenciesInPlace,
  } = dataX;

  const { exportToJson, importFromJson } = useGanttImportExport({ data, setData });

  const [editTaskId, setEditTaskId] = useState<string | null>(null);
  const [showImportText, setShowImportText] = useState(false);
  const [showImportJson, setShowImportJson] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [showShiftDialog, setShowShiftDialog] = useState(false);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);

  // Focus search input on Mod+Q or Alt+Q
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isHotkey('mod+q', e) || isHotkey('alt+q', e)) {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close actions dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (actionsRef.current && !actionsRef.current.contains(e.target as Node)) {
        setShowActions(false);
      }
    };
    if (showActions) {
      window.addEventListener('mousedown', handleClickOutside);
    }
    return () => window.removeEventListener('mousedown', handleClickOutside);
  }, [showActions]);

  const editTask = data.tasks.find(t => t.id === editTaskId) ?? null;

  const handleEditTask = useCallback((id: string) => setEditTaskId(id), []);
  const handleSaveTask = useCallback(
    (id: string, patch: Partial<Omit<Task, 'id'>>) => updateTask(id, patch),
    [updateTask]
  );
  const handleDeleteTask = useCallback((id: string) => removeTask(id), [removeTask]);
  const handleImportText = useCallback((titles: string[]) => addTasksBulk(titles), [addTasksBulk]);

  const { timelineStart, totalDays } = useMemo(() => {
    const startDates = data.tasks.map(t => t.startDate);
    const endDates = data.tasks.map(t => t.endDate);

    const min = minIso(startDates) ?? todayIso();
    const max = maxIso(endDates) ?? min;

    // Calculate diff in days. 
    // Ensure we don't get a negative number if for some reason max < min
    const days = Math.max(0, diffDays(min, max));

    return { 
      timelineStart: min, 
      totalDays: Math.max(days, TIMELINE_DAYS),
    };
  }, [data.tasks]);

  const [columnWidth, setColumnWidth] = useState<number>(COLUMN_WIDTH);
  const [filter, setFilter] = useState<string>("");

  const handleActionClick = (actionFn: () => void) => {
    actionFn();
    setShowActions(false);
  };

  const handleShiftSubmit = (days: number) => {
    shiftUnresolvedTasksInPlace(days);
    setShowShiftDialog(false);
  };

  return (
    <GanttContext.Provider value={{ timelineStart, columnWidth, timelineDays: totalDays, filter, setFilter }}>
      <div className="gantt-editor" style={{ display: 'flex', flexDirection: 'column', gap: 8, fontFamily: 'sans-serif', height: "100%" }}>
        {/* Toolbar */}
        <div className="toolbar" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '4px 0' }}>
          <button onClick={() => addTask('New Task')} style={btn('primary')}>
            + Add Task
          </button>

          <Sep />

          {/* Actions Dropdown */}
          <div ref={actionsRef} style={{ position: 'relative' }}>
            <button onClick={() => setShowActions((prev) => !prev)} style={btn('secondary')}>
              Actions ▾
            </button>
            
            {showActions && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: 4,
                zIndex: 1000,
                background: 'white',
                border: '1px solid #d0d0d0',
                borderRadius: 4,
                padding: '4px',
                display: 'flex',
                flexDirection: 'column',
                minWidth: '160px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }}>
                <button onClick={() => handleActionClick(() => setShowImportText(true))} style={menuBtn}>Import from text</button>
                <button onClick={() => handleActionClick(() => setShowImportJson(true))} style={menuBtn}>Import JSON</button>
                <button onClick={() => handleActionClick(exportToJson)} style={menuBtn}>Export JSON ↓</button>
                
                <div style={{ height: 1, background: '#e0e0e0', margin: '4px 0' }} />
                
                <button 
                  onClick={() => handleActionClick(autoScheduleInPlace)} 
                  style={{...menuBtn, opacity: data.tasks.length === 0 ? 0.5 : 1}} 
                  disabled={data.tasks.length === 0}
                >
                  Auto schedule
                </button>
                <button 
                  onClick={() => handleActionClick(autoArrangeInPlace)} 
                  style={{...menuBtn, opacity: data.tasks.length === 0 ? 0.5 : 1}} 
                  disabled={data.tasks.length === 0}
                >
                  Auto arrange
                </button>
                <button 
                  onClick={() => handleActionClick(expandToSingleTaskRowsInPlace)} 
                  style={{...menuBtn, opacity: data.tasks.length === 0 ? 0.5 : 1}} 
                  disabled={data.tasks.length === 0}
                >
                  1 task per row
                </button>

                <div style={{ height: 1, background: '#e0e0e0', margin: '4px 0' }} />

                <button 
                  onClick={() => handleActionClick(() => setShowShiftDialog(true))} 
                  style={{...menuBtn, opacity: data.tasks.length === 0 ? 0.5 : 1}} 
                  disabled={data.tasks.length === 0}
                >
                  Shift unresolved...
                </button>
                
                <button 
                  onClick={() => handleActionClick(shiftFromUnresolvedDependenciesInPlace)} 
                  style={{...menuBtn, opacity: data.tasks.length === 0 ? 0.5 : 1}} 
                  disabled={data.tasks.length === 0}
                >
                  Shift from incoming dependencies
                </button>
              </div>
            )}
          </div>

          <span style={{ fontSize: 12, color: '#aaa', marginLeft: 4 }}>
            {data.tasks.length} task{data.tasks.length !== 1 ? 's' : ''} · {data.dependencies.length} dep
            {data.dependencies.length !== 1 ? 's' : ''}
          </span>
          
          <OccupyFreeSpace />

          <input
            ref={searchInputRef}
            placeholder="Search (Alt+Q)"
            value={filter}
            onChange={(e) => setFilter(e.target.value?.toLowerCase() || "")}
          />

          <button onClick={() => setColumnWidth(Math.min(columnWidth*2, COLUMN_WIDTH*8))} style={btn('secondary')}>
            +
          </button>

          <button onClick={() => setColumnWidth(COLUMN_WIDTH)} style={btn('secondary')}>
            .
          </button>

          <button onClick={() => setColumnWidth(Math.max(columnWidth/2, COLUMN_WIDTH/8))} style={btn('secondary')}>
            -
          </button>

          {!!save && <>
            <button onClick={() => save()} style={btn('secondary')}>
              Save
            </button>
          </>}

        </div>

        {/* Chart */}
        <GanttContainer
          data={data}
          moveTaskDates={moveTaskDates}
          moveTaskToRow={moveTaskToRow}
          createRow={createRow}
          addDependency={addDependency}
          onEditTask={handleEditTask}
          hasUnresolvedDepsMap={dataX.hasUnresolvedDepsMap}
          addRowAbove={dataX.addRowAbove}
          addRowBelow={dataX.addRowBelow}
          splitRow={dataX.splitRow}
        />

        {/* Dialogs */}
        <TaskEditDialog
          task={editTask}
          onSave={handleSaveTask}
          onDelete={handleDeleteTask}
          onClose={() => setEditTaskId(null)}
          data={data}
          onRemoveDependency={removeDependency}
        />

        <ImportTextDialog open={showImportText} onImport={handleImportText} onClose={() => setShowImportText(false)} />

        <ImportJsonDialog open={showImportJson} onImport={importFromJson} onClose={() => setShowImportJson(false)} />

        {showShiftDialog && (
          <ShiftUnresolvedDialog 
            onClose={() => setShowShiftDialog(false)} 
            onSubmit={handleShiftSubmit} 
          />
        )}
      </div>
    </GanttContext.Provider>
  );
};


// --- Sub-components & Styles ---

const ShiftUnresolvedDialog: React.FC<{ onClose: () => void, onSubmit: (days: number) => void }> = ({ onClose, onSubmit }) => {
  const [days, setDays] = useState<number>(0);

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000
    }}>
      <div style={{ background: 'white', padding: 20, borderRadius: 8, width: 300, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>Shift unresolved tasks</h3>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>Days to shift (can be negative):</label>
          <input 
            type="number" 
            value={days} 
            onChange={(e) => setDays(Number(e.target.value))} 
            autoFocus
            style={{ width: '100%', padding: '6px', boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={btn('secondary')}>Cancel</button>
          <button onClick={() => onSubmit(days)} style={btn('primary')}>Apply</button>
        </div>
      </div>
    </div>
  );
};

const btn = (type: 'primary' | 'secondary'): React.CSSProperties => ({
  padding: '6px 12px',
  fontSize: 13,
  borderRadius: 4,
  cursor: 'pointer',
  border: type === 'primary' ? 'none' : '1px solid #d0d0d0',
  background: type === 'primary' ? '#3b82f6' : '#f5f5f5',
  color: type === 'primary' ? '#fff' : '#333',
});

const menuBtn: React.CSSProperties = {
  padding: '6px 12px',
  fontSize: 13,
  borderRadius: 4,
  cursor: 'pointer',
  border: 'none',
  background: 'transparent',
  textAlign: 'left',
  color: '#333',
};

const Sep: React.FC = () => <div style={{ width: 1, height: 20, background: '#e0e0e0', margin: '0 2px' }} />;
